/**
 * OrgManagementView — Main container for the Organization module.
 *
 * Tab-based layout with sections:
 *  - Users: Invite, list, manage org members
 *  - Departments: Create and organize departments
 *  - Structure: Visual org tree (reporting hierarchy)
 *  - Settings: Subscription, modules, org details
 */
import React, { useEffect, useCallback, useState } from 'react';
import Users from 'lucide-react/dist/esm/icons/users';
import Building2 from 'lucide-react/dist/esm/icons/building-2';
import GitBranch from 'lucide-react/dist/esm/icons/git-branch';
import Settings from 'lucide-react/dist/esm/icons/settings';
import UserPlus from 'lucide-react/dist/esm/icons/user-plus';
import Plus from 'lucide-react/dist/esm/icons/plus';
import Shield from 'lucide-react/dist/esm/icons/shield';
import Mail from 'lucide-react/dist/esm/icons/mail';
import AlertTriangle from 'lucide-react/dist/esm/icons/alert-triangle';
import ChevronRight from 'lucide-react/dist/esm/icons/chevron-right';
import Palette from 'lucide-react/dist/esm/icons/palette';
import Check from 'lucide-react/dist/esm/icons/check';
import RotateCcw from 'lucide-react/dist/esm/icons/rotate-ccw';
import type { LucideIcon } from 'lucide-react';
import { useOrgStore } from '../../../stores/orgStore';
import { useUIStore } from '../../../stores/uiStore';
import { useThemeStore } from '../../../stores/themeStore';
import { usePermissions } from '../../../hooks/usePermissions';
import { Permissions } from '../../../config/permissions';
import { getTranslation, getModuleLabel } from '../../../i18n';
import type { Department, OrgThemeConfig } from '../../../services/api/org';
import {
  updateOrgTheme,
  getSalesPublicApiKey,
  regenerateSalesPublicApiKey,
  getOrgLlmApiKey,
  setOrgLlmApiKey,
  clearOrgLlmApiKey,
} from '../../../services/api/org';
import type { LlmApiKeyStatus } from '../../../services/api/org';
import { useToast } from '../../../components/feedback';
import { usePagination } from '../../../hooks/usePagination';
import { Pagination, Select, SearchInput, Spinner } from '../../../components/ui';
import ModuleBadges from '../../../components/ui/ModuleBadges';
import { getModuleRegistry } from '../../../services/api/org';
import type { ModuleRegistryItem } from '../../../services/api/org';
import OrgTreeView from './OrgTreeView';
import DepartmentModal from './DepartmentModal';
import DepartmentDetailPanel from './DepartmentDetailPanel';
import UserDetailPanel from './UserDetailPanel';
import InviteUserModal from './InviteUserModal';
import AssignMemberPicker from './AssignMemberPicker';
import SalesPublicApiKeyBlock from './SalesPublicApiKeyBlock';
import LlmApiKeyBlock from './LlmApiKeyBlock';

type OrgView = 'users' | 'departments' | 'structure' | 'settings';

interface TabItem {
  id: OrgView;
  label: string;
  icon: LucideIcon;
}

const TABS: TabItem[] = [
  { id: 'users', label: 'People', icon: Users },
  { id: 'departments', label: 'Departments', icon: Building2 },
  { id: 'structure', label: 'Structure', icon: GitBranch },
  { id: 'settings', label: 'Settings', icon: Settings },
];

const ROLE_LABELS: Record<string, string> = {
  platform_admin: 'Platform Admin',
  tenant_admin: 'Tenant Admin',
  org_admin: 'Org Admin',
  user: 'User',
};

const ROLE_COLORS: Record<string, string> = {
  platform_admin: 'text-status-error bg-status-error/10 border-status-error/20',
  tenant_admin: 'text-purple-400 bg-purple-400/10 border-purple-400/20',
  org_admin: 'text-blue-400 bg-blue-400/10 border-blue-400/20',
  user: 'text-content-secondary bg-surface-primary border-default',
};

const STATUS_COLORS: Record<string, string> = {
  active: 'text-emerald-400',
  invited: 'text-amber-400',
  deactivated: 'text-content-muted',
};

const OrgManagementView: React.FC = () => {
  const {
    activeOrgView, setActiveOrgView,
    users, usersLoading, fetchUsers,
    departments, departmentsLoading, fetchDepartments,
    orgTree, orgTreeLoading, fetchOrgTree,
    userContext, fetchUserContext,
    inviteUser, updateUser, deactivateUser, setUserReporting,
  } = useOrgStore();

  // Panel / Modal state
  const [detailUser, setDetailUser] = useState<string | null>(null);
  const [showInvite, setShowInvite] = useState(false);
  const [inviteDefaultManager, setInviteDefaultManager] = useState<string | undefined>();
  const [inviteDefaultDept, setInviteDefaultDept] = useState<string | undefined>();
  const [assignTargetId, setAssignTargetId] = useState<string | null>(null);
  const [moduleRegistry, setModuleRegistry] = useState<ModuleRegistryItem[]>([]);

  useEffect(() => {
    fetchUserContext();
    fetchUsers();
    fetchDepartments();
    getModuleRegistry().then(setModuleRegistry).catch(() => {});
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (activeOrgView === 'structure') {
      fetchOrgTree();
    }
  }, [activeOrgView]); // eslint-disable-line react-hooks/exhaustive-deps

  // Permission-based access — all UI gating uses permissions, not roles
  const { hasPermission, hasAnyPermission } = usePermissions();
  const canManageMembers = hasPermission(Permissions.ORG_MEMBERS_MANAGE);
  const canViewSettings = hasPermission(Permissions.ORG_SETTINGS_VIEW);
  // Legacy compat: "isAdmin" means can manage anything in the org
  const isAdmin = hasAnyPermission(
    Permissions.ORG_MEMBERS_MANAGE,
    Permissions.ORG_DEPARTMENTS_MANAGE,
    Permissions.ORG_STRUCTURE_MANAGE,
  );

  // ── Handlers ──

  const handleSelectUser = useCallback((userId: string) => {
    setDetailUser(userId);
  }, []);

  const handleOpenInvite = useCallback((managerId?: string, deptId?: string) => {
    setInviteDefaultManager(managerId);
    setInviteDefaultDept(deptId);
    setShowInvite(true);
  }, []);

  const handleSaveUser = useCallback(async (userId: string, data: Record<string, any>) => {
    await updateUser(userId, data);
  }, [updateUser]);

  const handleSetReporting = useCallback(async (userId: string, managerId: string | null) => {
    await setUserReporting(userId, managerId);
  }, [setUserReporting]);

  const handleDeactivate = useCallback(async (userId: string) => {
    await deactivateUser(userId);
    setDetailUser(null);
  }, [deactivateUser]);

  const handleInvite = useCallback(async (data: Parameters<typeof inviteUser>[0]) => {
    const user = await inviteUser(data);
    return user;
  }, [inviteUser]);

  const handleAssignExisting = useCallback((managerId: string) => {
    setAssignTargetId(managerId);
  }, []);

  const handleAssignMember = useCallback(async (userId: string, managerId: string) => {
    await setUserReporting(userId, managerId);
  }, [setUserReporting]);

  // Resolve selected user object
  const selectedUser = detailUser ? users.find((u) => u.id === detailUser) || null : null;
  const assignTargetUser = assignTargetId ? users.find((u) => u.id === assignTargetId) || null : null;

  return (
    <div className="flex-1 flex flex-col animate-fade-in min-h-0">
      {/* Header */}
      <div className="flex items-center justify-between px-6 py-4 border-b border-subtle">
        <div>
          <h1 className="text-lg font-bold text-content">Organization</h1>
          <p className="text-xs text-content-muted mt-0.5">
            {userContext?.org_name || 'Manage your organization'}
          </p>
        </div>
        {canManageMembers && (
          <button
            onClick={() => handleOpenInvite()}
            className="flex items-center gap-2 px-3 py-2 rounded-lg bg-status-success/10 text-status-success text-xs font-medium hover:bg-status-success/20 transition-colors border border-status-success/20 cursor-pointer"
          >
            <UserPlus size={14} />
            Invite Member
          </button>
        )}
      </div>

      {/* Tab Navigation */}
      <div className="flex items-center gap-1 px-6 py-2 border-b border-subtle">
        {TABS.map((tab) => {
          const isActive = activeOrgView === tab.id;
          const Icon = tab.icon;
          if (tab.id === 'settings' && !canViewSettings) return null;
          return (
            <button
              key={tab.id}
              onClick={() => setActiveOrgView(tab.id)}
              className={`
                flex items-center gap-2 px-3 py-2 rounded-lg text-xs font-medium
                transition-all duration-150 border-none outline-none cursor-pointer
                ${isActive
                  ? 'bg-status-success/10 text-status-success'
                  : 'text-content-tertiary hover:text-content-secondary hover:bg-surface-primary'
                }
              `}
            >
              <Icon size={14} />
              {tab.label}
            </button>
          );
        })}
      </div>

      {/* Content Area */}
      <div className="flex-1 overflow-y-auto p-6 min-h-0">
        {activeOrgView === 'users' && (
          <UsersPanel
            users={users}
            loading={usersLoading}
            isAdmin={isAdmin}
            departments={departments}
            onSelectUser={handleSelectUser}
            onInvite={() => handleOpenInvite()}
          />
        )}
        {activeOrgView === 'departments' && (
          <DepartmentsPanel
            departments={departments}
            users={users}
            loading={departmentsLoading}
            isAdmin={isAdmin}
          />
        )}
        {activeOrgView === 'structure' && (
          <OrgTreeView
            tree={orgTree}
            loading={orgTreeLoading}
            users={users}
            isAdmin={isAdmin}
            onSelectUser={handleSelectUser}
            onReassignManager={handleSetReporting}
            onInviteUnder={(managerId) => handleOpenInvite(managerId)}
            onAssignExisting={handleAssignExisting}
          />
        )}
        {activeOrgView === 'settings' && canViewSettings && (
          <SettingsPanel />
        )}
      </div>

      {/* ── User Detail Panel (slide-out) ── */}
      <UserDetailPanel
        user={selectedUser}
        isOpen={!!detailUser}
        onClose={() => setDetailUser(null)}
        isAdmin={isAdmin}
        users={users}
        departments={departments}
        onSave={handleSaveUser}
        onSetReporting={handleSetReporting}
        onDeactivate={isAdmin ? handleDeactivate : undefined}
        orgEnabledModules={userContext?.enabled_modules || []}
        moduleRegistry={moduleRegistry}
      />

      {/* ── Invite User Modal ── */}
      <InviteUserModal
        isOpen={showInvite}
        onClose={() => setShowInvite(false)}
        users={users}
        departments={departments}
        onInvite={handleInvite}
        defaultManagerId={inviteDefaultManager}
        defaultDepartmentId={inviteDefaultDept}
        orgEnabledModules={userContext?.enabled_modules || []}
        moduleRegistry={moduleRegistry}
      />

      {/* ── Assign Existing Member Picker ── */}
      <AssignMemberPicker
        isOpen={!!assignTargetId}
        onClose={() => setAssignTargetId(null)}
        targetUser={assignTargetUser}
        users={users}
        onAssign={handleAssignMember}
      />
    </div>
  );
};

// ═══ Users Panel ═══

const USERS_PAGE_SIZE = 20;

const UsersPanel: React.FC<{
  users: any[];
  loading: boolean;
  isAdmin: boolean;
  departments: any[];
  onSelectUser: (userId: string) => void;
  onInvite: () => void;
}> = ({ users, loading, isAdmin, departments, onSelectUser, onInvite }) => {
  const [search, setSearch] = useState('');
  const [filterDept, setFilterDept] = useState<string>('all');
  const [filterRole, setFilterRole] = useState<string>('all');

  // Apply search and filters
  const filteredUsers = React.useMemo(() => {
    let result = users;
    const q = search.toLowerCase();
    if (q) {
      result = result.filter(
        (u: any) =>
          (u.display_name || '').toLowerCase().includes(q) ||
          u.email.toLowerCase().includes(q) ||
          (u.title || '').toLowerCase().includes(q)
      );
    }
    if (filterDept !== 'all') {
      if (filterDept === 'none') {
        result = result.filter((u: any) => !u.department_id);
      } else {
        result = result.filter((u: any) => u.department_id === filterDept);
      }
    }
    if (filterRole !== 'all') {
      result = result.filter((u: any) => u.system_role === filterRole);
    }
    return result;
  }, [users, search, filterDept, filterRole]);

  const {
    page, pageItems, totalPages, totalItems,
    startIndex, endIndex, setPage,
  } = usePagination(filteredUsers, { pageSize: USERS_PAGE_SIZE });

  const getDeptName = useCallback((deptId: string | null) => {
    if (!deptId) return null;
    const dept = departments.find((d: any) => d.id === deptId);
    return dept?.name || null;
  }, [departments]);

  const getManagerName = useCallback((managerId: string | null) => {
    if (!managerId) return null;
    const mgr = users.find((u: any) => u.id === managerId);
    return mgr ? (mgr.display_name || mgr.email.split('@')[0]) : null;
  }, [users]);

  // Count unassigned users
  const unassignedCount = React.useMemo(
    () => users.filter((u: any) => !u.department_id && !u.reports_to).length,
    [users]
  );

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <div className="w-6 h-6 border-2 border-status-success/30 border-t-success rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Search + Filters Row */}
      <div className="flex items-center gap-3 flex-wrap">
        <div className="flex-1 min-w-[200px]">
          <SearchInput
            value={search}
            onChange={(val) => { setSearch(val); setPage(1); }}
            placeholder="Search by name, email, or title..."
          />
        </div>
        <Select
          value={filterDept}
          onChange={(val) => { setFilterDept(val); setPage(1); }}
          options={[
            { value: 'all', label: 'All departments' },
            { value: 'none', label: 'Unassigned' },
            ...departments.map((d: any) => ({ value: d.id, label: d.name })),
          ]}
          placeholder="All departments"
          size="sm"
          className="min-w-[160px]"
        />
        <Select
          value={filterRole}
          onChange={(val) => { setFilterRole(val); setPage(1); }}
          options={[
            { value: 'all', label: 'All roles' },
            ...Object.entries(ROLE_LABELS).map(([val, label]) => ({ value: val, label })),
          ]}
          placeholder="All roles"
          size="sm"
          className="min-w-[140px]"
        />
      </div>

      {/* Stats row */}
      <div className="flex items-center gap-4 text-xs">
        <span className="text-content-muted">{totalItems} member{totalItems !== 1 ? 's' : ''}</span>
        {unassignedCount > 0 && (
          <span className="flex items-center gap-1 text-amber-400/70">
            <AlertTriangle size={11} />
            {unassignedCount} unassigned
          </span>
        )}
      </div>

      {/* User Cards */}
      <div className="grid gap-2">
        {pageItems.map((user: any) => {
          const deptName = getDeptName(user.department_id);
          const mgrName = getManagerName(user.reports_to);
          const isUnassigned = !user.department_id && !user.reports_to;

          return (
            <button
              key={user.id}
              type="button"
              onClick={() => onSelectUser(user.id)}
              className={`
                flex items-center gap-4 p-3 rounded-xl border transition-all text-left w-full outline-none cursor-pointer
                ${isUnassigned
                  ? 'bg-amber-400/[0.02] border-amber-400/10 hover:border-amber-400/20'
                  : 'bg-surface-primary border-subtle hover:border-default hover:bg-surface-primary'
                }
              `}
            >
              {/* Avatar */}
              <div className="w-10 h-10 rounded-full bg-gradient-to-br from-brand/30 to-status-success/30 flex items-center justify-center text-xs font-bold text-content-inverse shrink-0">
                {(user.display_name || user.email)[0]?.toUpperCase()}
              </div>

              {/* Info */}
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2">
                  <span className="text-sm font-medium text-content truncate">
                    {user.display_name || user.email.split('@')[0]}
                  </span>
                  {user.status !== 'active' && (
                    <span className={`text-[10px] px-1.5 py-0.5 rounded ${STATUS_COLORS[user.status] || 'text-content-muted'}`}>
                      {user.status}
                    </span>
                  )}
                  {isUnassigned && (
                    <AlertTriangle size={11} className="text-amber-400/50 shrink-0" />
                  )}
                </div>
                <div className="flex items-center gap-2 mt-0.5 flex-wrap">
                  {user.title && (
                    <span className="text-xs text-content-muted truncate">{user.title}</span>
                  )}
                  {user.title && (deptName || mgrName) && <span className="text-content-faint">·</span>}
                  {deptName && (
                    <span className="inline-flex items-center gap-1 text-[10px] text-brand/50">
                      <Building2 size={9} />
                      {deptName}
                    </span>
                  )}
                  {mgrName && (
                    <span className="inline-flex items-center gap-1 text-[10px] text-content-faint">
                      <GitBranch size={9} />
                      {mgrName}
                    </span>
                  )}
                </div>
              </div>

              {/* Module Badges */}
              {user.module_access?.length > 0 && (
                <ModuleBadges modules={user.module_access} size="sm" />
              )}

              {/* Role Badge */}
              <span className={`px-2 py-0.5 rounded-md text-[10px] font-medium border shrink-0 ${ROLE_COLORS[user.system_role] || ROLE_COLORS.user}`}>
                {ROLE_LABELS[user.system_role] || user.system_role}
              </span>

              <ChevronRight size={14} className="text-content-faint shrink-0" />
            </button>
          );
        })}

        {totalItems === 0 && (
          <div className="text-center py-16">
            <Users size={32} className="text-content-faint mx-auto mb-3" />
            <p className="text-sm text-content-muted mb-1">
              {search || filterDept !== 'all' || filterRole !== 'all' ? 'No matching members' : 'No team members yet'}
            </p>
            <p className="text-xs text-content-faint">
              {search || filterDept !== 'all' || filterRole !== 'all'
                ? 'Try adjusting your search or filters'
                : 'Invite people to get started'
              }
            </p>
          </div>
        )}
      </div>

      {/* Pagination */}
      <Pagination
        page={page}
        totalPages={totalPages}
        onPageChange={setPage}
        totalItems={totalItems}
        startIndex={startIndex}
        endIndex={endIndex}
      />
    </div>
  );
};

// ═══ Departments Panel ═══

const DepartmentsPanel: React.FC<{
  departments: Department[];
  users: any[];
  loading: boolean;
  isAdmin: boolean;
}> = ({ departments, users, loading, isAdmin }) => {
  const { addDepartment, updateDepartment, updateUser, removeDepartment, fetchUsers } = useOrgStore();
  const [createModalOpen, setCreateModalOpen] = useState(false);
  const [detailDept, setDetailDept] = useState<Department | null>(null);

  const getMemberCount = useCallback(
    (deptId: string) => users.filter((u) => u.department_id === deptId).length,
    [users]
  );

  const handleCreate = () => {
    setCreateModalOpen(true);
  };

  const handleOpenDetail = (dept: Department) => {
    setDetailDept(dept);
  };

  const handleCreateSave = async (name: string, memberIds: string[]) => {
    await addDepartment(name);
    if (memberIds.length > 0) {
      const { departments: latestDepts } = useOrgStore.getState();
      const newDept = latestDepts.find((d) => d.name === name);
      if (newDept) {
        for (const uid of memberIds) {
          await updateUser(uid, { department_id: newDept.id });
        }
        await fetchUsers();
      }
    }
  };

  const handleEditSave = async (name: string, memberIds: string[]) => {
    if (!detailDept) return;
    // Update name if changed
    if (name !== detailDept.name) {
      await updateDepartment(detailDept.id, { name });
    }
    // Compute member diffs
    const currentIds = users
      .filter((u) => u.department_id === detailDept.id)
      .map((u) => u.id);
    const nextIds = memberIds;
    const currentSet = new Set(currentIds);
    const nextSet = new Set(nextIds);
    // Add new members
    const toAdd = nextIds.filter((uid) => !currentSet.has(uid));
    for (let i = 0; i < toAdd.length; i++) {
      await updateUser(toAdd[i], { department_id: detailDept.id });
    }
    // Remove old members (send null to clear department)
    const toRemove = currentIds.filter((uid) => !nextSet.has(uid));
    for (let i = 0; i < toRemove.length; i++) {
      await updateUser(toRemove[i], { department_id: null as any });
    }
    // Refresh users to reflect department changes
    await fetchUsers();
  };

  const handleDelete = () => {
    if (detailDept) {
      removeDepartment(detailDept.id);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <div className="w-6 h-6 border-2 border-status-success/30 border-t-success rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <>
      <div className="space-y-4">
        <div className="flex items-center justify-between">
          <div className="text-sm text-content-secondary">
            {departments.length} department{departments.length !== 1 ? 's' : ''}
          </div>
          {isAdmin && (
            <button
              onClick={handleCreate}
              className="flex items-center gap-2 px-3 py-2 rounded-lg bg-status-success/10 text-status-success text-xs font-medium hover:bg-status-success/20 transition-colors border border-status-success/20 cursor-pointer"
            >
              <Plus size={14} />
              Add Department
            </button>
          )}
        </div>

        <div className="grid gap-2">
          {departments.map((dept) => {
            const count = getMemberCount(dept.id);
            return (
              <button
                key={dept.id}
                type="button"
                onClick={() => isAdmin && handleOpenDetail(dept)}
                className={`
                  flex items-center justify-between p-4 rounded-xl bg-surface-primary border border-subtle
                  transition-all text-left w-full outline-none
                  ${isAdmin ? 'hover:border-default hover:bg-surface-primary cursor-pointer' : 'cursor-default'}
                `}
              >
                <div className="flex items-center gap-3">
                  <div className="w-9 h-9 rounded-lg bg-brand/10 flex items-center justify-center">
                    <Building2 size={16} className="text-brand/60" />
                  </div>
                  <div>
                    <span className="text-sm font-medium text-content block">{dept.name}</span>
                    <span className="text-[11px] text-content-muted">
                      {count} member{count !== 1 ? 's' : ''}
                    </span>
                  </div>
                </div>
                {isAdmin && (
                  <ChevronRight size={14} className="text-content-faint" />
                )}
              </button>
            );
          })}

          {departments.length === 0 && (
            <div className="text-center py-16">
              <Building2 size={32} className="text-content-faint mx-auto mb-3" />
              <p className="text-sm text-content-muted mb-1">No departments yet</p>
              <p className="text-xs text-content-faint">Create departments to organize your team</p>
            </div>
          )}
        </div>
      </div>

      {/* Department Create Modal */}
      <DepartmentModal
        isOpen={createModalOpen}
        onClose={() => setCreateModalOpen(false)}
        department={null}
        users={users}
        onSave={handleCreateSave}
      />

      {/* Department Detail Drawer (edit existing) */}
      <DepartmentDetailPanel
        department={detailDept}
        isOpen={!!detailDept}
        onClose={() => setDetailDept(null)}
        isAdmin={isAdmin}
        users={users}
        onSave={handleEditSave}
        onDelete={isAdmin ? handleDelete : undefined}
      />
    </>
  );
};

// ═══ Settings Panel ═══

const PRESET_COLORS = [
  '#219EBC', '#6366f1', '#8b5cf6', '#ec4899', '#f43f5e',
  '#ef4444', '#f97316', '#eab308', '#22c55e', '#14b8a6',
  '#06b6d4', '#3b82f6', '#1d4ed8', '#7c3aed', '#a855f7',
];

const ColorSwatch: React.FC<{ color: string; selected: boolean; onClick: () => void }> = ({ color, selected, onClick }) => (
  <button
    onClick={onClick}
    className={`w-8 h-8 rounded-lg border-2 transition-all cursor-pointer outline-none ${selected ? 'border-content scale-110 shadow-md' : 'border-transparent hover:scale-105'}`}
    style={{ backgroundColor: color }}
  />
);

const ThemePanel: React.FC = () => {
  const { userContext, fetchUserContext } = useOrgStore();
  const { setOrgTheme } = useThemeStore();
  const isAdmin = userContext?.system_role !== 'user';

  const currentTheme = userContext?.theme_config;
  const [primary, setPrimary] = useState(currentTheme?.primary_color || '#219EBC');
  const [secondary, setSecondary] = useState(currentTheme?.secondary_color || '#10b981');
  const [logoUrl, setLogoUrl] = useState(currentTheme?.logo_url || '');
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);

  const handlePreview = useCallback(() => {
    setOrgTheme({ primary_color: primary, secondary_color: secondary, logo_url: logoUrl || null });
  }, [primary, secondary, logoUrl, setOrgTheme]);

  useEffect(() => { handlePreview(); }, [primary, secondary]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleSave = useCallback(async () => {
    setSaving(true);
    try {
      const theme: OrgThemeConfig = {
        primary_color: primary === '#219EBC' ? null : primary,
        secondary_color: secondary === '#10b981' ? null : secondary,
        logo_url: logoUrl || null,
      };
      await updateOrgTheme(theme);
      await fetchUserContext();
      setSaved(true);
      setTimeout(() => setSaved(false), 2000);
    } catch {
      // Silent fail — toast could be added
    } finally {
      setSaving(false);
    }
  }, [primary, secondary, logoUrl, fetchUserContext]);

  const handleReset = useCallback(() => {
    setPrimary('#219EBC');
    setSecondary('#10b981');
    setLogoUrl('');
    setOrgTheme(null);
  }, [setOrgTheme]);

  if (!isAdmin) return null;

  return (
    <div className="p-5 rounded-2xl bg-surface-primary border border-subtle">
      <h3 className="text-sm font-semibold text-content mb-4 flex items-center gap-2">
        <Palette size={16} className="text-brand" />
        Brand Customization
      </h3>

      <div className="space-y-5">
        {/* Primary Color */}
        <div>
          <label className="text-[11px] text-content-tertiary uppercase tracking-wider block mb-2">Primary Color</label>
          <div className="flex items-center gap-3 flex-wrap">
            {PRESET_COLORS.map(c => (
              <ColorSwatch key={c} color={c} selected={primary === c} onClick={() => setPrimary(c)} />
            ))}
            <div className="flex items-center gap-2 ml-2">
              <input
                type="color"
                value={primary}
                onChange={e => setPrimary(e.target.value)}
                className="w-8 h-8 rounded-lg cursor-pointer border border-subtle bg-transparent"
              />
              <span className="text-xs text-content-muted font-mono">{primary}</span>
            </div>
          </div>
        </div>

        {/* Secondary Color */}
        <div>
          <label className="text-[11px] text-content-tertiary uppercase tracking-wider block mb-2">Secondary / Accent</label>
          <div className="flex items-center gap-2">
            <input
              type="color"
              value={secondary}
              onChange={e => setSecondary(e.target.value)}
              className="w-8 h-8 rounded-lg cursor-pointer border border-subtle bg-transparent"
            />
            <span className="text-xs text-content-muted font-mono">{secondary}</span>
          </div>
        </div>

        {/* Logo URL */}
        <div>
          <label className="text-[11px] text-content-tertiary uppercase tracking-wider block mb-2">Logo URL</label>
          <input
            type="url"
            value={logoUrl}
            onChange={e => setLogoUrl(e.target.value)}
            placeholder="https://example.com/logo.svg"
            className="w-full px-3 py-2.5 rounded-xl bg-surface-secondary border border-subtle text-content text-xs placeholder:text-content-muted outline-none focus:border-brand/40 transition-all"
          />
        </div>

        {/* Preview swatch */}
        <div className="flex items-center gap-3 p-3 rounded-xl bg-surface-secondary border border-subtle">
          <div className="w-10 h-10 rounded-xl flex items-center justify-center text-content-inverse text-xs font-bold" style={{ background: `linear-gradient(135deg, ${primary}, ${secondary})` }}>
            Aa
          </div>
          <div className="flex-1">
            <div className="text-xs font-medium text-content">Live Preview</div>
            <div className="text-[11px] text-content-muted">Colors update as you pick</div>
          </div>
        </div>

        {/* Actions */}
        <div className="flex items-center gap-3 pt-2">
          <button
            onClick={handleSave}
            disabled={saving}
            className="flex items-center gap-2 px-4 py-2 rounded-lg text-xs font-medium text-content-inverse transition-colors cursor-pointer border-none outline-none disabled:opacity-50"
            style={{ background: `linear-gradient(135deg, ${primary}, ${secondary})` }}
          >
            {saving ? <Spinner size="sm" /> : saved ? <Check size={12} /> : null}
            {saving ? 'Saving...' : saved ? 'Saved!' : 'Save Theme'}
          </button>
          <button
            onClick={handleReset}
            className="flex items-center gap-2 px-4 py-2 rounded-lg text-xs font-medium text-content-muted hover:text-content-secondary bg-surface-secondary hover:bg-surface-elevated transition-colors cursor-pointer border border-subtle outline-none"
          >
            <RotateCcw size={12} />
            Reset to Default
          </button>
        </div>
      </div>
    </div>
  );
};

const SettingsPanel: React.FC = () => {
  const { userContext } = useOrgStore();
  const { language } = useUIStore();
  const { success, error } = useToast();
  const tr = (key: string) => getTranslation(language, key);

  const isAdmin = ['org_admin', 'tenant_admin', 'platform_admin'].includes(userContext?.system_role || '');
  const [apiKey, setApiKey] = useState<string | null>(null);
  const [apiKeyLoading, setApiKeyLoading] = useState(false);
  const [apiKeyError, setApiKeyError] = useState(false);
  const [regeneratingKey, setRegeneratingKey] = useState(false);
  const [llmStatus, setLlmStatus] = useState<LlmApiKeyStatus | null>(null);
  const [llmLoading, setLlmLoading] = useState(false);
  const [llmError, setLlmError] = useState(false);
  const [llmSaving, setLlmSaving] = useState(false);
  const [llmClearing, setLlmClearing] = useState(false);

  const orgIdForApi = userContext?.org_id;

  const loadApiKey = useCallback(async () => {
    setApiKeyLoading(true);
    setApiKeyError(false);
    try {
      // Platform admins must pass org_id; other roles may omit (backend uses token org).
      const r = await getSalesPublicApiKey(orgIdForApi);
      setApiKey(r.public_api_key);
    } catch {
      setApiKeyError(true);
    } finally {
      setApiKeyLoading(false);
    }
  }, [orgIdForApi]);

  const loadLlmKey = useCallback(async () => {
    setLlmLoading(true);
    setLlmError(false);
    try {
      const r = await getOrgLlmApiKey(orgIdForApi);
      setLlmStatus(r);
    } catch {
      setLlmError(true);
    } finally {
      setLlmLoading(false);
    }
  }, [orgIdForApi]);

  useEffect(() => {
    if (!isAdmin) return;
    void loadApiKey();
    void loadLlmKey();
  }, [isAdmin, loadApiKey, loadLlmKey]);

  const handleRegenerate = useCallback(async () => {
    setRegeneratingKey(true);
    try {
      const r = await regenerateSalesPublicApiKey(orgIdForApi);
      setApiKey(r.public_api_key);
      setApiKeyError(false);
      success(tr('settingsOrgsLeadApiKeyRegenerateSuccess'));
    } catch (e: unknown) {
      const msg =
        (e as { response?: { data?: { detail?: string } } })?.response?.data?.detail ||
        tr('settingsOrgsLeadApiKeyRegenerateError');
      error(String(msg));
      throw e;
    } finally {
      setRegeneratingKey(false);
    }
  }, [error, orgIdForApi, success, tr]);

  const handleSaveLlmKey = useCallback(async (key: string) => {
    setLlmSaving(true);
    try {
      const r = await setOrgLlmApiKey(key, orgIdForApi);
      setLlmStatus(r);
      setLlmError(false);
      success(tr('settingsLlmSaveSuccess'));
    } catch (e: unknown) {
      const msg =
        (e as { response?: { data?: { detail?: string } } })?.response?.data?.detail ||
        tr('settingsLlmSaveError');
      error(String(msg));
      throw e;
    } finally {
      setLlmSaving(false);
    }
  }, [error, orgIdForApi, success, tr]);

  const handleClearLlmKey = useCallback(async () => {
    setLlmClearing(true);
    try {
      const r = await clearOrgLlmApiKey(orgIdForApi);
      setLlmStatus(r);
      setLlmError(false);
      success(tr('settingsLlmClearSuccess'));
    } catch (e: unknown) {
      const msg =
        (e as { response?: { data?: { detail?: string } } })?.response?.data?.detail ||
        tr('settingsLlmClearError');
      error(String(msg));
      throw e;
    } finally {
      setLlmClearing(false);
    }
  }, [error, orgIdForApi, success, tr]);

  return (
    <div className="space-y-6 max-w-2xl">
      {/* Theme Customization (admin only) */}
      <ThemePanel />

      {/* Organization Info */}
      <div className="p-5 rounded-2xl bg-surface-primary border border-subtle">
        <h3 className="text-sm font-semibold text-content mb-4 flex items-center gap-2">
          <Building2 size={16} className="text-primary" />
          Organization Details
        </h3>
        <div className="grid grid-cols-2 gap-4">
          <div>
            <label className="text-[11px] text-content-tertiary uppercase tracking-wider">Name</label>
            <p className="text-sm text-content mt-1">{userContext?.org_name || '—'}</p>
          </div>
          <div>
            <label className="text-[11px] text-content-tertiary uppercase tracking-wider">Your Role</label>
            <p className="text-sm text-content mt-1">
              {ROLE_LABELS[userContext?.system_role || 'user']}
            </p>
          </div>
        </div>
      </div>

      {isAdmin && (
        <LlmApiKeyBlock
          tr={tr}
          status={llmStatus}
          loading={llmLoading}
          error={llmError}
          saving={llmSaving}
          clearing={llmClearing}
          orgScope
          onSave={handleSaveLlmKey}
          onClear={handleClearLlmKey}
        />
      )}

      {/* Lead Form API Key (admins only) */}
      {isAdmin && (
        <SalesPublicApiKeyBlock
          tr={tr}
          apiKey={apiKey}
          loading={apiKeyLoading}
          error={apiKeyError}
          onRegenerate={handleRegenerate}
          regenerating={regeneratingKey}
        />
      )}

      {/* Enabled Modules */}
      <div className="p-5 rounded-2xl bg-surface-primary border border-subtle">
        <h3 className="text-sm font-semibold text-content mb-4 flex items-center gap-2">
          <Shield size={16} className="text-status-success" />
          {getTranslation(language, 'enabledModules')}
        </h3>
        <div className="space-y-2">
          {(userContext?.enabled_modules || []).length > 0 ? (
            userContext!.enabled_modules.map((mod) => (
              <div key={mod} className="flex items-center gap-2 text-sm text-content-secondary">
                <ChevronRight size={12} className="text-status-success" />
                {getModuleLabel(mod, language)}
              </div>
            ))
          ) : (
            <p className="text-xs text-content-muted">{getTranslation(language, 'noModulesEnabled')}</p>
          )}
        </div>
      </div>

      {/* Account Info */}
      <div className="p-5 rounded-2xl bg-surface-primary border border-subtle">
        <h3 className="text-sm font-semibold text-content mb-4 flex items-center gap-2">
          <Mail size={16} className="text-amber-400" />
          Your Account
        </h3>
        <div className="grid grid-cols-2 gap-4">
          <div>
            <label className="text-[11px] text-content-tertiary uppercase tracking-wider">Email</label>
            <p className="text-sm text-content mt-1">{userContext?.email || '—'}</p>
          </div>
          <div>
            <label className="text-[11px] text-content-tertiary uppercase tracking-wider">Display Name</label>
            <p className="text-sm text-content mt-1">{userContext?.display_name || '—'}</p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default React.memo(OrgManagementView);
